// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Decision logic for the catalog browse-first entry (AUT-5713): which CTAs the create-project
// form offers, and when the project page auto-opens the catalog order form. Kept free of
// component imports so the logic is unit-testable on its own.

import { aoiPresent } from './orderState';

// Query param the Browse Products CTA appends to the project URL; the project Sidebar
// consumes it once the draft order exists and then strips it, so refreshes and shared
// links don't re-open the form.
export const CATALOG_BROWSE_PARAM = 'browse';

interface CreateProjectCtaInputs {
  isAdmin: boolean;
  projectType: string;
  utilitiesCatalog: boolean | undefined;
}

interface CreateProjectCtas {
  showBrowseProducts: boolean;
  showCreateEstimate: boolean;
}

// A customer on the catalog flag is a utilities customer: the create-project type picker
// defaults to Utilities for them (Joel, 2026-09-01), which also puts BROWSE PRODUCTS on
// screen the moment the dialog opens. Everyone else (admin portal included) keeps today's
// pick-a-type-first behavior.
export const resolveDefaultProjectType = ({ isAdmin, utilitiesCatalog }: { isAdmin: boolean; utilitiesCatalog: boolean | undefined }): string =>
  (!isAdmin && utilitiesCatalog === true ? 'utilities' : '');

// Browse-first replaces the AOI-first CREATE ESTIMATE entry for flag-on Utilities projects
// (two-button layout per the reviewed Figma); every other state keeps today's buttons, and
// the admin portal never offers either (it doesn't today).
export const resolveCreateProjectCtas = ({ isAdmin, projectType, utilitiesCatalog }: CreateProjectCtaInputs): CreateProjectCtas => {
  const showBrowseProducts = !isAdmin && projectType === 'utilities' && utilitiesCatalog === true;
  return {
    showBrowseProducts,
    showCreateEstimate: !isAdmin && !showBrowseProducts,
  };
};

interface SidebarOrderCtaInputs {
  projectType: string | undefined;
  utilitiesCatalog: boolean | undefined;
  order: IOrder | null | undefined;
}

type SidebarOrderCtaLabel = 'BROWSE PRODUCTS' | 'CONTINUE ORDER' | 'CREATE ORDER';

interface SidebarOrderCta {
  label: SidebarOrderCtaLabel;
  // The same words as prose, for copy that names the button — 'Click "Continue Order" to…'.
  // One source, so a card can never name a button the sidebar does not show (AUT-6630 F1).
  prose: string;
  browse: boolean;
}

const PROSE: Record<SidebarOrderCtaLabel, string> = {
  'BROWSE PRODUCTS': 'Browse Products',
  'CONTINUE ORDER': 'Continue Order',
  'CREATE ORDER': 'Create Order',
};

const cta = (label: SidebarOrderCtaLabel, browse: boolean): SidebarOrderCta => ({ label, prose: PROSE[label], browse });

// The project sidebar's order button. A flag-on Utilities project has two labels (AUT-6630
// D18): BROWSE PRODUCTS while the AOI isn't in place (orderState.aoiPresent — the one rule the
// sidebar, the banner and the server share), opening the catalog in browse mode instead of
// sitting disabled; CONTINUE ORDER from then on — the form restores a saved selection or
// shows the default product either way, so "create" and "continue" were never two different
// steps. Everything else (AEC, flag-off) keeps the legacy CREATE ORDER.
export const resolveSidebarOrderCta = ({ projectType, utilitiesCatalog, order }: SidebarOrderCtaInputs): SidebarOrderCta => {
  const catalogUtilities = utilitiesCatalog === true && projectType === 'utilities';
  if (catalogUtilities && !!order && !aoiPresent(order)) {
    return cta('BROWSE PRODUCTS', true);
  }
  if (catalogUtilities && !!order) {
    return cta('CONTINUE ORDER', false);
  }
  return cta('CREATE ORDER', false);
};

interface AutoOpenBrowseInputs {
  browseRequested: boolean;
  orderId: string | undefined;
  projectType: string | undefined;
  utilitiesCatalog: boolean | undefined;
}

// The order form needs the project's draft order in the store before it can open (its load
// reads the activeOrder), so the auto-open waits for one. The type/flag guards make a
// hand-pasted ?browse=1 inert on AEC projects and flag-off accounts.
export const shouldAutoOpenCatalogBrowse = ({ browseRequested, orderId, projectType, utilitiesCatalog }: AutoOpenBrowseInputs): boolean =>
  browseRequested && !!orderId && projectType === 'utilities' && utilitiesCatalog === true;
